import React from 'react';
import { Bold, Italic, Underline, Highlighter, Baseline } from 'lucide-react';

/**
 * Barre de mise en forme d'un message d'email — la seule de l'application.
 *
 * Les marqueurs sont ceux que le serveur interprète dans `templateBodyToHtml` : `**gras**`,
 * `*italique*`, `__souligné__`, `[[taille=Npx]]…[[/taille]]`, `[[couleur=#rrggbb]]…[[/couleur]]`,
 * `[[surlignage=#rrggbb]]…[[/surlignage]]`, `[[police=Nom]]…[[/police]]`. Aucun format n'existe ici
 * qui n'existe pas à l'envoi : ce qu'on voit dans l'éditeur est ce qui part.
 *
 * `wrapNotificationSelection` vit ici et non dans l'écran des notifications Prestation, où elle était
 * née : ce fichier est importé par le bloc email générique, et l'inverse aurait créé un cycle
 * d'imports entre un composant du socle et un écran métier. L'ancien chemin d'import reste valide,
 * PrestationNotificationsPage la ré-exporte.
 */

export const wrapNotificationSelection = (
    el: HTMLTextAreaElement | null,
    value: string,
    setValue: (v: string) => void,
    openMarker: string,
    closeMarker: string = openMarker,
) => {
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + openMarker + selected + closeMarker + value.slice(end);
    setValue(next);
    if (el) {
        requestAnimationFrame(() => {
            el.focus();
            const pos = selected ? start + openMarker.length + selected.length + closeMarker.length : start + openMarker.length;
            el.setSelectionRange(pos, pos);
        });
    }
};

/** Tailles proposées, en px — interprétées par le marqueur [[taille=Npx]]. */
export const FONT_SIZE_OPTIONS = [
    { value: '12', label: 'Petit' },
    { value: '16', label: 'Normal' },
    { value: '20', label: 'Grand' },
    { value: '28', label: 'Très grand' },
];

/**
 * Polices proposées. Ce sont des familles avec repli, pas des polices exotiques : un email s'affiche
 * avec ce que la machine du destinataire possède déjà. Une police téléchargée ne s'affiche dans aucun
 * client mail sérieux, autant ne pas la promettre.
 */
const POLICES = [
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
    { value: '"Trebuchet MS", Tahoma, sans-serif', label: 'Trebuchet' },
    { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
    { value: '"Courier New", Courier, monospace', label: 'Courier' },
];

const COULEURS_TEXTE = ['#0f1419', '#384049', '#8a1c1c', '#1f6b45', '#2159c7', '#987b55'];
const COULEURS_SURLIGNAGE = ['#ffe39b', '#c8e6c9', '#bbdefb', '#f8bbd0', '#e1bee7', '#ffccbc'];

export function MessageFormatBar({ textareaRef, value, onChange }: {
    textareaRef: React.RefObject<HTMLTextAreaElement | null>;
    value: string;
    onChange: (v: string) => void;
}) {
    const [palette, setPalette] = React.useState<'couleur' | 'surlignage' | null>(null);
    const btn = 'p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content';
    const entourer = (ouvrant: string, fermant?: string) =>
        wrapNotificationSelection(textareaRef.current, value, onChange, ouvrant, fermant);

    const choisir = (hex: string) => {
        if (palette === 'couleur') entourer(`[[couleur=${hex}]]`, '[[/couleur]]');
        else entourer(`[[surlignage=${hex}]]`, '[[/surlignage]]');
        setPalette(null);
    };

    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-1 flex-wrap">
                <button type="button" title="Gras" className={btn} onClick={() => entourer('**')}><Bold size={12} /></button>
                <button type="button" title="Italique" className={btn} onClick={() => entourer('*')}><Italic size={12} /></button>
                <button type="button" title="Souligné" className={btn} onClick={() => entourer('__')}><Underline size={12} /></button>

                <button type="button" title="Couleur du texte"
                    className={`${btn} ${palette === 'couleur' ? 'border-content text-content' : ''}`}
                    onClick={() => setPalette((p) => (p === 'couleur' ? null : 'couleur'))}><Baseline size={12} /></button>
                <button type="button" title="Surligner"
                    className={`${btn} ${palette === 'surlignage' ? 'border-content text-content' : ''}`}
                    onClick={() => setPalette((p) => (p === 'surlignage' ? null : 'surlignage'))}><Highlighter size={12} /></button>

                <select defaultValue="" title="Taille du texte sélectionné"
                    onChange={(e) => { if (e.target.value) { entourer(`[[taille=${e.target.value}px]]`, '[[/taille]]'); e.target.value = ''; } }}
                    className="p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content text-[11px] outline-none">
                    <option value="" disabled>Taille</option>
                    {FONT_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>

                <select defaultValue="" title="Police du texte sélectionné"
                    onChange={(e) => { if (e.target.value) { entourer(`[[police=${e.target.value}]]`, '[[/police]]'); e.target.value = ''; } }}
                    className="p-1.5 rounded-md border border-border bg-surface-soft text-content-muted hover:text-content text-[11px] outline-none">
                    <option value="" disabled>Police</option>
                    {POLICES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>

                <span className="text-[10px] text-content-muted ml-1">Sélectionnez du texte puis cliquez.</span>
            </div>

            {palette && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    {(palette === 'couleur' ? COULEURS_TEXTE : COULEURS_SURLIGNAGE).map((hex) => (
                        <button key={hex} type="button" title={hex} onClick={() => choisir(hex)}
                            style={{ background: hex }}
                            className="w-[22px] h-[22px] rounded-[6px] border border-border shrink-0" />
                    ))}
                    {/* Le sélecteur natif pour tout le reste : six pastilles couvrent l'usage courant,
                        pas la charte d'un établissement qui tient à son rouge exact. */}
                    <input type="color" title="Autre couleur"
                        onChange={(e) => choisir(e.target.value)}
                        className="h-[22px] w-[28px] rounded-[6px] border border-border bg-surface-soft p-0.5 cursor-pointer shrink-0" />
                </div>
            )}
        </div>
    );
}
